import { Fragment } from "react"

interface FrameworkGroupedListProps<T> {
  groups: ReadonlyArray<readonly [string, T[]]>
  registerSection: (framework: string) => (el: HTMLDivElement | null) => void
  keyOf: (item: T) => string
  renderItem: (item: T) => React.ReactNode
  /** Rendered before the groups (e.g. a coverage banner). */
  topContent?: React.ReactNode
}

/**
 * Renders one labeled section per framework group, in first-seen order.
 * Takes its groups from `useFrameworkGroups` (shared with `FrameworkChips`,
 * which lives outside this panel) — shared between GapsStep and ReviewStep
 * so both left panels behave the same way instead of each inventing its own
 * layout.
 */
export function FrameworkGroupedList<T>({
  groups,
  registerSection,
  keyOf,
  renderItem,
  topContent,
}: FrameworkGroupedListProps<T>) {
  return (
    <>
      {topContent}

      {groups.map(([framework, groupItems]) => (
        <div key={framework} ref={registerSection(framework)}>
          <div className="sticky top-0 z-10 flex items-center gap-1.5 border-b bg-background px-4 py-2">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              {framework}
              <span className="ml-1.5 font-normal normal-case text-muted-foreground/70">
                · {groupItems.length}
              </span>
            </p>
          </div>
          {groupItems.map((item) => (
            <Fragment key={keyOf(item)}>{renderItem(item)}</Fragment>
          ))}
        </div>
      ))}
    </>
  )
}
